import React, { useEffect, useState } from "react";
import { ChevronLeft, CloudOff, RefreshCw } from "lucide-react";
import { pending, flush } from "./techData";
import { cn } from "../lib/utils";

export const C = { navy: "#0f2f52", aqua: "#06b6d4", ink: "#0b1726" };

export function SyncPill() {
  const [n, setN] = useState(0), [on, setOn] = useState(navigator.onLine), [err, setErr] = useState("");
  useEffect(() => {
    const f = () => { pending().then(setN).catch(() => {}); setOn(navigator.onLine); setErr(localStorage.getItem("tech:last-error") || ""); };
    f(); ["tech:queue", "online", "offline"].forEach((e) => window.addEventListener(e, f));
    const t = setInterval(f, 5000);
    return () => { clearInterval(t); ["tech:queue", "online", "offline"].forEach((e) => window.removeEventListener(e, f)); };
  }, []);
  if (err && on && !n) return <button onClick={() => { localStorage.removeItem("tech:last-error"); setErr(""); }} data-testid="tech-sync-error" className="max-w-[55%] truncate rounded-full px-3 py-1.5 text-[12px] font-black bg-red-500 text-white" title={err}>{err.startsWith("Text not sent") ? err : `Not saved: ${err}`} ✕</button>;
  if (on && !n) return null;
  return (
    <button onClick={flush} data-testid="tech-sync-pill" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-black ${on ? "bg-amber-400 text-amber-950" : "bg-white/15 text-white"}`}>
      {on ? <RefreshCw className="h-4 w-4" /> : <CloudOff className="h-4 w-4" />}{on ? `Sending ${n}…` : `No signal${n ? ` · ${n} saved` : ""}`}
    </button>
  );
}

export function Screen({ title, sub, onBack, children, footer, testid, right, rightClass = "w-[66%] max-w-[340px]" }) {
  return (
    <div className="min-h-[100dvh] flex flex-col bg-slate-100" data-testid={testid}>
      <header className="sticky top-0 z-20 text-white px-3 pt-[max(env(safe-area-inset-top),10px)] pb-3 flex items-center gap-2 shadow-lg" style={{ background: C.navy }}>
        {onBack && <button onClick={onBack} data-testid="tech-back" aria-label="Back" className="h-12 w-12 -ml-1 rounded-2xl flex items-center justify-center active:bg-white/20"><ChevronLeft className="h-8 w-8" /></button>}
        <div className="flex-1 min-w-0">
          <div className={`${right ? (title.length > 8 ? "text-[19px]" : "text-[24px]") : "text-[21px]"} font-black leading-tight ${right ? "line-clamp-2 break-words" : "truncate"}`} data-testid="tech-title">{title}</div>
          {sub && <div className="text-[13px] text-sky-200 truncate">{sub}</div>}
          {right && <div className="mt-1"><SyncPill /></div>}
        </div>
        {right ? <div className={`shrink-0 ${rightClass}`}>{right}</div> : <SyncPill />}
      </header>
      <main className={`flex-1 p-3 space-y-3 ${footer ? "pb-44" : "pb-6"}`}>{children}</main>
      {footer && <div className="sticky bottom-0 z-20 p-3 pb-[max(env(safe-area-inset-bottom),12px)] bg-white/95 backdrop-blur border-t border-slate-200">{footer}</div>}
    </div>
  );
}

export const Big = ({ onClick, children, tone = "navy", testid, disabled, className = "" }) => {
  const t = { navy: "bg-[#0f2f52] text-white", aqua: "bg-cyan-500 text-white", green: "bg-green-600 text-white", red: "bg-red-600 text-white", white: "bg-white text-[#0f2f52] border-2 border-slate-200", amber: "bg-amber-400 text-amber-950" }[tone];
  return <button onClick={onClick} disabled={disabled} data-testid={testid} className={`w-full min-h-[64px] rounded-2xl px-4 text-[19px] font-black flex items-center justify-center gap-2.5 shadow-sm active:scale-[0.98] transition-transform disabled:opacity-40 ${t} ${className}`}>{children}</button>;
};

// one big square button on the stop screen; done = green tick
export const Tile = ({ icon: Icon, label, onClick, done, testid, color = "#0f2f52" }) => (
  <button onClick={onClick} data-testid={testid} className={`relative rounded-3xl min-h-[118px] p-3 flex flex-col items-center justify-center gap-2 shadow-sm active:scale-[0.97] transition-transform border-2 ${done ? "bg-green-50 border-green-500" : "bg-white border-transparent"}`}>
    <Icon className="h-10 w-10" style={{ color: done ? "#16a34a" : color }} strokeWidth={2.2} />
    <span className="text-[16px] font-black text-slate-800 text-center leading-tight">{label}</span>
    {done && <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-green-600 text-white text-[16px] font-black flex items-center justify-center">✓</span>}
  </button>
);

export const Card = ({ children, className = "", ...rest }) => <div {...rest} className={cn("bg-white rounded-2xl p-4 shadow-sm", className)}>{children}</div>;
